import { useNavigate, useParams } from '@tanstack/react-router'
import type { GroupUpdate } from '@topmeeps/shared'
import { useEffect, useState } from 'react'
import { ApiError } from '../api/client'
import { useDeleteGroup, useEditGroup, useGroup, useLeaveGroup } from '../api/hooks'
import { GuestsSection } from '../components/GuestsSection'
import { ScreenShell } from '../components/ScreenShell'
import { BackButton, SectionHeader, TopBar } from '../primitives'

export const GroupSettingsScreen = () => {
  const { id } = useParams({ from: '/group/$id/settings' })
  const navigate = useNavigate()
  const group = useGroup(id)

  if (group.isLoading) {
    return (
      <ScreenShell>
        <div style={{ padding: 16, color: 'var(--ink-3)' }}>Loading…</div>
      </ScreenShell>
    )
  }
  if (!group.data) {
    return (
      <ScreenShell>
        <div style={{ padding: 16, color: 'var(--ink-2)' }}>Group not found.</div>
      </ScreenShell>
    )
  }

  const isAdmin = group.data.role === 'admin'

  return (
    <ScreenShell>
      <TopBar
        left={<BackButton fallback={() => navigate({ to: '/group/$id', params: { id } })} />}
        title="Settings"
        sub={group.data.name}
      />

      {isAdmin && <IdentitySection groupId={id} group={group.data} />}

      <GuestsSection groupId={id} />

      <LeaveSection groupId={id} viewerIsAdmin={isAdmin} navigate={navigate} />

      {isAdmin && <DangerZone groupId={id} groupName={group.data.name} navigate={navigate} />}
    </ScreenShell>
  )
}

const IdentitySection = ({
  groupId,
  group,
}: {
  groupId: string
  group: { name: string; description: string | null }
}) => {
  const edit = useEditGroup(groupId)
  const [name, setName] = useState(group.name)
  const [description, setDescription] = useState(group.description ?? '')
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  // If the group data refreshes from elsewhere, re-sync.
  useEffect(() => {
    setName(group.name)
    setDescription(group.description ?? '')
  }, [group.name, group.description])

  const dirty = name.trim() !== group.name || (description || null) !== (group.description ?? null)

  const onSave = async () => {
    setError(null)
    if (!name.trim()) {
      setError('Name is required')
      return
    }
    const payload: GroupUpdate = {
      name: name.trim(),
      description: description.trim() ? description.trim() : null,
    }
    try {
      await edit.mutateAsync(payload)
      setSavedAt(Date.now())
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Could not save')
    }
  }

  return (
    <>
      <SectionHeader kicker="identity" title="Group" />
      <div style={{ padding: '0 16px' }}>
        <div
          className="felt-card"
          style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <Field label="Name">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              style={inputStyle}
            />
          </Field>
          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              style={{ ...inputStyle, fontFamily: 'var(--font-display)', resize: 'vertical' }}
            />
          </Field>
          {error && <div style={{ fontSize: 12, color: 'var(--terra-ink)' }}>{error}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="btn-stamp"
              onClick={onSave}
              disabled={edit.isPending || !dirty}
            >
              {edit.isPending ? 'Saving…' : 'Save changes'}
            </button>
            {savedAt && !dirty && (
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Saved.</span>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

const LeaveSection = ({
  groupId,
  viewerIsAdmin,
  navigate,
}: {
  groupId: string
  viewerIsAdmin: boolean
  navigate: ReturnType<typeof useNavigate>
}) => {
  const leave = useLeaveGroup(groupId)
  const [confirm, setConfirm] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onLeave = async () => {
    setError(null)
    try {
      await leave.mutateAsync()
      navigate({ to: '/home' })
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Could not leave')
      setConfirm(false)
    }
  }

  return (
    <>
      <SectionHeader kicker={viewerIsAdmin ? 'your membership' : 'leave'} title="Leave group" />
      <div style={{ padding: '0 16px' }}>
        <div
          className="felt-card"
          style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            Your historical plays stay in the group. Your per-group rating freezes; you can be
            re-invited later and pick up where you left off.
            {viewerIsAdmin && ' If you are the only admin, promote another member first.'}
          </div>
          {error && <div style={{ fontSize: 12, color: 'var(--terra-ink)' }}>{error}</div>}
          {!confirm ? (
            <button type="button" className="btn-ghost" onClick={() => setConfirm(true)}>
              Leave this group
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setConfirm(false)}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-stamp"
                onClick={onLeave}
                disabled={leave.isPending}
                style={{ flex: 1, background: 'var(--terra)', borderColor: 'var(--terra)' }}
              >
                {leave.isPending ? 'Leaving…' : 'Yes, leave'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const DangerZone = ({
  groupId,
  groupName,
  navigate,
}: {
  groupId: string
  groupName: string
  navigate: ReturnType<typeof useNavigate>
}) => {
  const del = useDeleteGroup(groupId)
  const [confirmText, setConfirmText] = useState('')
  const [error, setError] = useState<string | null>(null)

  const onDelete = async () => {
    setError(null)
    try {
      await del.mutateAsync()
      navigate({ to: '/home' })
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Could not delete')
    }
  }

  return (
    <>
      <SectionHeader kicker="danger zone" title="Delete group" />
      <div style={{ padding: '0 16px' }}>
        <div
          style={{
            padding: 14,
            background: 'var(--terra-bg)',
            border: '1px solid var(--terra)',
            borderRadius: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ fontSize: 13, color: 'var(--terra-ink)' }}>
            Soft-deletes the group. Plays and ratings are preserved but the group is hidden from
            everyone. Type the group name to confirm.
          </div>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={groupName}
            style={inputStyle}
          />
          {error && <div style={{ fontSize: 12, color: 'var(--terra-ink)' }}>{error}</div>}
          <button
            type="button"
            className="btn-stamp"
            onClick={onDelete}
            disabled={confirmText !== groupName || del.isPending}
            style={{
              background: confirmText === groupName ? 'var(--terra)' : 'var(--ink-3)',
              borderColor: 'var(--terra)',
            }}
          >
            {del.isPending ? 'Deleting…' : 'Delete this group'}
          </button>
        </div>
      </div>
    </>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--paper)',
  border: '1px solid var(--line-soft)',
  borderRadius: 4,
  fontFamily: 'var(--font-display)',
  fontSize: 14,
  color: 'var(--ink)',
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div className="kicker" style={{ fontSize: 11 }}>
      {label}
    </div>
    <div style={{ marginTop: 4 }}>{children}</div>
  </div>
)
